import React, { useState, useEffect } from "react";
import { Lock, User, Users, Plus, Info, Crown, Eye, Clock } from "lucide-react";
import { useUsuarios } from "../context/UsuariosContext";
import { Usuario } from "../../../types";
import UsuarioForm from "../components/UsuarioForm";
import UsuariosList from "../components/UsuariosList";
import ModalSenhaTemporaria from "../components/ModalSenhaTemporaria";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";

export default function GestaoUsuarios() {
  const { usuarioLogado } = useAuth();
  const {
    usuarios,
    loading,
    senhaTemporaria, // ⭐ NOVO
    adicionarUsuario,
    editarUsuario,
    excluirUsuario,
    bloquearUsuario,
    limparSenhaTemporaria, // ⭐ NOVO
    carregarUsuarios, // ⭐ ADICIONAR ESTA FUNÇÃO
  } = useUsuarios();

  const [modoEdicao, setModoEdicao] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState<Usuario | undefined>(
    undefined
  );
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [emailUsuarioCadastrado, setEmailUsuarioCadastrado] = useState(""); // ⭐ NOVO

  // ⭐ CARREGAR USUÁRIOS AO ABRIR A PÁGINA
  useEffect(() => {
    if (usuarioLogado) {
      console.log("📋 Carregando usuários na Gestão...");
      carregarUsuarios();
    }
  }, [usuarioLogado]);

  // ⭐ NOVO: Fechar modal de senha
  const handleFecharModalSenha = () => {
    limparSenhaTemporaria();
    setEmailUsuarioCadastrado("");
  };

  // 🔒 VERIFICAR SE USUÁRIO É ADMINISTRADOR
  if (!usuarioLogado || usuarioLogado.tipo !== "administrador") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          background: "white",
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.md,
          padding: "48px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Lock
            size={80}
            color={theme.gray[300]}
            style={{ marginBottom: "16px" }}
          />
          <h3
            style={{
              fontSize: "24px",
              color: theme.gray[700],
              marginBottom: "8px",
              fontFamily: theme.fonts.sans,
            }}
          >
            Acesso Restrito
          </h3>
          <p
            style={{
              color: theme.gray[500],
              fontFamily: theme.fonts.sans,
            }}
          >
            Apenas administradores podem acessar a gestão de usuários
          </p>
        </div>
      </div>
    );
  }

  // ➕ ABRIR FORMULÁRIO PARA NOVO USUÁRIO
  const handleNovoUsuario = () => {
    setUsuarioEditando(undefined);
    setModoEdicao(false);
    setMostrarFormulario(true);
  };

  // ✏️ ABRIR FORMULÁRIO PARA EDITAR USUÁRIO
  const handleEditarUsuario = (usuario: Usuario) => {
    setUsuarioEditando(usuario);
    setModoEdicao(true);
    setMostrarFormulario(true);
  };

  // 💾 SALVAR USUÁRIO (NOVO OU EDITADO)
  const handleSalvarUsuario = async (
    usuario: Omit<Usuario, "id" | "criadoEm">
  ) => {
    try {
      if (modoEdicao && usuarioEditando) {
        // Editar usuário existente
        await editarUsuario(usuarioEditando.id, usuario);
        alert("✅ Usuário atualizado com sucesso!");
      } else {
        // ⭐ Adicionar novo usuário (vai gerar senha)
        setEmailUsuarioCadastrado(usuario.email); // Salvar email para o modal
        await adicionarUsuario(usuario);
        // NÃO mostra alert aqui - o modal de senha será mostrado
      }

      // Fechar formulário
      setMostrarFormulario(false);
      setUsuarioEditando(undefined);
      setModoEdicao(false);
    } catch (error: any) {
      alert(`❌ Erro: ${error.message || "Erro ao salvar usuário"}`);
    }
  };

  // ❌ CANCELAR EDIÇÃO
  const handleCancelar = () => {
    setMostrarFormulario(false);
    setUsuarioEditando(undefined);
    setModoEdicao(false);
  };

  // 🗑️ EXCLUIR USUÁRIO
  const handleExcluirUsuario = (id: string) => {
    excluirUsuario(id);
  };

  // 🔒 BLOQUEAR/DESBLOQUEAR USUÁRIO
  const handleBloquearUsuario = (id: string, bloquear: boolean) => {
    bloquearUsuario(id, bloquear);
  };

  return (
    <div>
      {/* ⭐ MODAL DE SENHA TEMPORÁRIA */}
      {senhaTemporaria && emailUsuarioCadastrado && (
        <ModalSenhaTemporaria
          senha={senhaTemporaria}
          email={emailUsuarioCadastrado}
          onClose={handleFecharModalSenha}
        />
      )}

      {/* CABEÇALHO */}
      <div
        style={{
          background: "white",
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.md,
          padding: "24px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "28px",
                fontWeight: "bold",
                color: theme.gray[800],
                margin: 0,
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <Users size={22} style={{ marginRight: "10px" }} /> Gestão de
              Usuários
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: theme.gray[600],
                marginTop: "4px",
                fontFamily: theme.fonts.sans,
              }}
            >
              Gerencie os usuários que têm acesso ao sistema
            </p>
          </div>

          {!mostrarFormulario && (
            <button
              onClick={handleNovoUsuario}
              style={{
                padding: "12px 24px",
                background: theme.primary[600],
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.primary[700];
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = theme.primary[600];
              }}
            >
              <Plus size={14} style={{ marginRight: "6px" }} /> Novo Usuário
            </button>
          )}
        </div>
      </div>

      {/* FORMULÁRIO OU LISTA */}
      {mostrarFormulario ? (
        <UsuarioForm
          onSubmit={handleSalvarUsuario}
          onCancel={handleCancelar}
          initialData={usuarioEditando}
          usuarioLogadoId={usuarioLogado.id}
        />
      ) : loading ? (
        <div
          style={{
            background: "white",
            borderRadius: theme.borderRadius.xl,
            boxShadow: theme.shadows.md,
            padding: "48px",
            textAlign: "center",
          }}
        >
          <p style={{ color: theme.gray[600], fontFamily: theme.fonts.sans }}>
          <Clock size={14} style={{ marginRight: "6px" }} /> Carregando usuários...
          </p>
        </div>
      ) : usuarios && usuarios.length > 0 ? (
        <UsuariosList
          usuarios={usuarios}
          usuarioLogadoId={usuarioLogado.id}
          onEditar={handleEditarUsuario}
          onExcluir={handleExcluirUsuario}
          onBloquear={handleBloquearUsuario}
        />
      ) : (
        <div
          style={{
            background: "white",
            borderRadius: theme.borderRadius.xl,
            boxShadow: theme.shadows.md,
            padding: "48px",
            textAlign: "center",
          }}
        >
          <p style={{ color: theme.gray[600], fontFamily: theme.fonts.sans }}>
            📭 Nenhum usuário cadastrado ainda.
          </p>
        </div>
      )}

      {/* INFORMAÇÕES ÚTEIS */}
      {!mostrarFormulario && (
        <div
          style={{
            background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[800]} 100%)`,
            borderRadius: theme.borderRadius.xl,
            boxShadow: theme.shadows.md,
            padding: "24px",
            marginTop: "24px",
            color: "white",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              margin: "0 0 12px 0",
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Info size={18} style={{ marginRight: "8px" }} /> Informações sobre
            Tipos de Usuário
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.1)",
                padding: "16px",
                borderRadius: theme.borderRadius.md,
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  margin: "0 0 8px 0",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Crown size={14} style={{ marginRight: "6px" }} /> Administrador
              </p>
              <p
                style={{
                  fontSize: "13px",
                  margin: 0,
                  opacity: 0.9,
                  fontFamily: theme.fonts.sans,
                }}
              >
                Acesso total ao sistema. Único que pode gerenciar usuários,
                cadastrar pacientes e registrar consultas.
              </p>
            </div>

            <div
              style={{
                background: "rgba(255,255,255,0.1)",
                padding: "16px",
                borderRadius: theme.borderRadius.md,
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  margin: "0 0 8px 0",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <User size={14} style={{ marginRight: "6px" }} /> Usuário
              </p>
              <p
                style={{
                  fontSize: "13px",
                  margin: 0,
                  opacity: 0.9,
                  fontFamily: theme.fonts.sans,
                }}
              >
                Pode cadastrar e editar pacientes, registrar consultas e exames.
                Não pode gerenciar outros usuários.
              </p>
            </div>

            <div
              style={{
                background: "rgba(255,255,255,0.1)",
                padding: "16px",
                borderRadius: theme.borderRadius.md,
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  margin: "0 0 8px 0",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Eye size={14} style={{ marginRight: "6px" }} /> Convidado
              </p>
              <p
                style={{
                  fontSize: "13px",
                  margin: 0,
                  opacity: 0.9,
                  fontFamily: theme.fonts.sans,
                }}
              >
                Apenas leitura. Pode visualizar pacientes e consultas, mas não
                pode editar ou adicionar dados.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
